import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import { getHomeData } from "@/api/homeApi";
import { assetUrl } from "@/utils/assetUrl";

const footerLinks = {
  solutions: [
    { label: "信息安全综合防护", path: "/solutions#info-security" },
    { label: "IT基础架构规划建设", path: "/solutions#it-infrastructure" },
    { label: "弱电智能化系统集成", path: "/solutions#intelligent-building" },
  ],
  products: [
    { label: "下一代防火墙", path: "/products" },
    { label: "入侵检测系统", path: "/products" },
    { label: "终端安全管理", path: "/products" },
    { label: "安全运营平台", path: "/products" },
  ],
  company: [
    { label: "关于我们", path: "/about" },
    { label: "成功案例", path: "/cases" },
    // { label: "行业资讯", path: "/news" }, // 暂时隐藏，后续可能会用
    { label: "联系我们", path: "/#contact-form" },
  ],
};

export default function Footer() {
  const companyInfo = useCompanyInfo();
  const [footerTags, setFooterTags] = useState<string[]>([]);

  useEffect(() => {
    getHomeData()
      .then((res: any) => {
        const tags = (res?.footerTags || res?.data?.footerTags || [])
          .map((t: any) => t.name)
          .filter(Boolean);
        setFooterTags(tags);
      })
      .catch(() => {});
  }, []);

  return (
    <footer className="text-gray-400" style={{ backgroundColor: "#081020" }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            {/* Logo */}
            <div className="mb-4">
              {(companyInfo.logoDark || companyInfo.logo) ? (
                <img src={assetUrl(companyInfo.logoDark || companyInfo.logo)} alt={companyInfo.companyName} className="h-8 max-w-[160px] object-contain" />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#1a56db]">
                    <i className="ri-shield-check-line text-lg text-white"></i>
                  </div>
                  <span className="text-white text-sm font-bold">{companyInfo.companyName}</span>
                </div>
              )}
            </div>
            <p className="text-sm leading-relaxed mb-5" style={{ color: "#A0AEC0" }}>
              {companyInfo.slogan}
            </p>
            {footerTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {footerTags.map((cert) => (
                  <span
                    key={cert}
                    className="px-2.5 py-1 text-xs border border-[#1a56db]/50 text-[#4db8ff] rounded-full"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Solutions */}
          <div>
            <h4 className="text-white text-sm font-bold mb-4 tracking-wider uppercase">解决方案</h4>
            <ul className="space-y-2.5">
              {footerLinks.solutions.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-sm transition-colors duration-200 hover:text-[#1a56db]"
                    style={{ color: "#A0AEC0" }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white text-sm font-bold mb-4 tracking-wider uppercase">产品服务</h4>
            <ul className="space-y-2.5">
              {footerLinks.products.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-sm transition-colors duration-200 hover:text-[#1a56db]"
                    style={{ color: "#A0AEC0" }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white text-sm font-bold mb-4 tracking-wider uppercase">联系我们</h4>
            <ul className="space-y-3">
              {companyInfo.phone && (
                <li className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-phone-line text-[#4db8ff] text-sm"></i>
                  </div>
                  <span className="text-sm" style={{ color: "#A0AEC0" }}>{companyInfo.phone}</span>
                </li>
              )}
              {companyInfo.email && (
                <li className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-mail-line text-[#4db8ff] text-sm"></i>
                  </div>
                  <span className="text-sm" style={{ color: "#A0AEC0" }}>{companyInfo.email}</span>
                </li>
              )}
              {companyInfo.address && (
                <li className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-map-pin-line text-[#4db8ff] text-sm"></i>
                  </div>
                  <span className="text-sm" style={{ color: "#A0AEC0" }}>{companyInfo.address}</span>
                </li>
              )}
            </ul>
            <div className="flex gap-3 mt-5">
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full border border-white/10 hover:text-[#4db8ff] hover:border-[#4db8ff]/50 transition-all duration-200 cursor-pointer" style={{ color: "#A0AEC0" }}>
                <i className="ri-wechat-line text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full border border-white/10 hover:text-[#4db8ff] hover:border-[#4db8ff]/50 transition-all duration-200 cursor-pointer" style={{ color: "#A0AEC0" }}>
                <i className="ri-linkedin-line text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full border border-white/10 hover:text-[#4db8ff] hover:border-[#4db8ff]/50 transition-all duration-200 cursor-pointer" style={{ color: "#A0AEC0" }}>
                <i className="ri-weibo-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>

        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <p className="text-xs" style={{ color: "#4A5568" }}>
            © {new Date().getFullYear()} {companyInfo.companyName} 版权所有 |{" "}
            <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer" className="hover:text-[#4db8ff] transition-colors">粤ICP备2025494075号-1</a>
            {" "}|{" "}
            <a href="#" className="hover:text-[#4db8ff] transition-colors">隐私政策</a>
          </p>
          <p className="text-xs" style={{ color: "#4A5568" }}>专业守护 · 安全无忧</p>
        </div>
      </div>
    </footer>
  );
}
